/**
 * SupportQueue — admin dashboard for the Mi6/Attilius support queue.
 *
 * Shows the open ticket queue at a glance for Bryan and Yvonne.
 * Reads from `support_tickets` (tier, assigned_to, auto_response_sent
 * columns added by the classifier agent) and `support_scan_log`
 * (created by the polling-cron agent).
 *
 * Palette LOCKED: #0B0B0F black · #DC2626 red · #FAFAF7 bg · slate text.
 * Card chrome matches existing dashboards: white, rounded-xl,
 * border-neutral-200, shadow-sm.
 *
 * Hash-routed at /#/admin/support. Manager + executive only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { api, getSession } from "@/lib/session";

// ─── Brand tokens ────────────────────────────────────────────────────────────
const MI6_BLACK = "#0B0B0F";
const MI6_RED = "#DC2626";
const MI6_SLATE = "#6B6B70";

// ─── Types ───────────────────────────────────────────────────────────────────
type Status = "open" | "in_progress" | "resolved";
type Tier = 1 | 2 | 3;
type AssignedTo = "computer" | "yvonne" | "bryan" | null;

interface SupportTicket {
  id: number;
  tenant_id?: number | null;
  user_id?: string | null;
  user_email?: string | null;
  subject: string;
  description: string;
  page?: string | null;
  screenshot_url?: string | null;
  status: Status;
  tier?: Tier | null;
  assigned_to?: AssignedTo;
  auto_response_sent?: boolean | null;
  resolved_at?: string | null;
  created_at: string;
  updated_at?: string | null;
  audit_log?: AuditEntry[] | null;
}

interface AuditEntry {
  at: string;
  actor: string;
  action: string;
  detail?: string;
}

interface ScanLogEntry {
  id: number | string;
  ran_at: string;
  tickets_scanned: number;
  escalations_fired: number;
  notes?: string | null;
}

interface ListResponse {
  tickets: SupportTicket[];
  totals: {
    open: number;
    in_progress: number;
    resolved: number;
    tier1_auto_resolved_today: number;
    tier1_seen_today: number;
    tier2_open: number;
    tier2_oldest_age_min: number | null;
    tier3_open: number;
    tier3_oldest_age_min: number | null;
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function relTime(iso?: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function ageMinutesToText(min: number | null): string {
  if (min == null) return "—";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function tierColors(tier: Tier | null | undefined) {
  if (tier === 3) return { bg: "#FEE2E2", border: "#FCA5A5", fg: MI6_RED, label: "T3" };
  if (tier === 2) return { bg: "#EFEFEE", border: "#C9C9CC", fg: "#525258", label: "T2" };
  return { bg: "#0B0B0F14", border: "#0B0B0F33", fg: MI6_BLACK, label: "T1" };
}

function statusColors(status: Status) {
  if (status === "resolved") return { bg: "#ECFDF5", fg: "#065F46", label: "Resolved" };
  if (status === "in_progress") return { bg: "#FEF3C7", fg: "#92400E", label: "In Progress" };
  return { bg: "#FAFAF7", fg: "#0B0B0F", label: "Open" };
}

function assignedLabel(a: AssignedTo): string {
  if (a === "computer") return "Computer";
  if (a === "yvonne") return "Yvonne";
  if (a === "bryan") return "Bryan";
  return "Unassigned";
}

// ─── KPI tile (matches ManagerView TeamKpiTile) ──────────────────────────────
function KpiTile({
  label,
  value,
  sub,
  accent = "navy",
  valueColor,
}: {
  label: string;
  value: string;
  sub: string;
  accent?: "navy" | "red" | "slate";
  valueColor?: string;
}) {
  const accentColor = accent === "red" ? MI6_RED : accent === "slate" ? "#6B6B70" : MI6_BLACK;
  return (
    <div
      className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5 border-l-4"
      style={{ borderLeftColor: accentColor }}
    >
      <div
        className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2"
        style={{ color: MI6_SLATE }}
      >
        {label}
      </div>
      <div
        className="text-3xl font-extrabold tabular-nums leading-none"
        style={{ color: valueColor ?? MI6_BLACK }}
      >
        {value}
      </div>
      <div className="text-xs mt-1.5" style={{ color: MI6_SLATE }}>
        {sub}
      </div>
    </div>
  );
}

// ─── Pill button ─────────────────────────────────────────────────────────────
function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "text-[11px] px-3 py-1.5 rounded-full border transition-all duration-150 font-medium whitespace-nowrap",
        active
          ? "bg-[#0B0B0F] border-[#0B0B0F] text-white"
          : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:text-neutral-800",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function SupportQueue() {
  const session = getSession();
  const role = session?.role;
  const isAuthorized = role === "manager" || role === ("executive" as any);

  const [statusFilter, setStatusFilter] = useState<"all" | Status>("open");
  const [tierFilter, setTierFilter] = useState<"all" | Tier>("all");
  const [data, setData] = useState<ListResponse | null>(null);
  const [scanLog, setScanLog] = useState<ScanLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const qs = new URLSearchParams();
      if (statusFilter !== "all") qs.set("status", statusFilter);
      if (tierFilter !== "all") qs.set("tier", String(tierFilter));
      const [list, scan] = await Promise.all([
        api<ListResponse>(`/api/support-tickets?${qs.toString()}`),
        api<{ scans: ScanLogEntry[] }>(`/api/support-scan-log?limit=20`).catch(
          () => ({ scans: [] as ScanLogEntry[] })
        ),
      ]);
      setData(list);
      setScanLog(scan.scans ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Could not load support queue.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, tierFilter]);

  useEffect(() => {
    if (!isAuthorized) return;
    fetchAll();
    const t = setInterval(fetchAll, 60_000); // light auto-refresh
    return () => clearInterval(t);
  }, [fetchAll, isAuthorized]);

  // ── Action handlers ────────────────────────────────────────────────────────
  const reRunAutoHandler = useCallback(
    async (id: number) => {
      try {
        setBusyId(id);
        await api(`/api/support-tickets/${id}/process`, { method: "POST" });
        await fetchAll();
      } catch (e: any) {
        setError(e?.message ?? "Re-run failed.");
      } finally {
        setBusyId(null);
      }
    },
    [fetchAll]
  );

  const patchTicket = useCallback(
    async (id: number, patch: Partial<Pick<SupportTicket, "status" | "tier" | "assigned_to">>) => {
      try {
        setBusyId(id);
        await api(`/api/support-tickets/${id}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        });
        await fetchAll();
      } catch (e: any) {
        setError(e?.message ?? "Update failed.");
      } finally {
        setBusyId(null);
      }
    },
    [fetchAll]
  );

  // ── Render guards ──────────────────────────────────────────────────────────
  if (!isAuthorized) {
    return (
      <OperatorShell breadcrumbs={[{ label: "Support Queue" }]}>
        <main className="max-w-3xl mx-auto px-6 py-16">
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-8 text-center">
            <div className="text-sm font-bold text-neutral-900 mb-1">Access restricted</div>
            <div className="text-xs text-neutral-500">
              Only managers and executives can view the support queue.
            </div>
          </div>
        </main>
      </OperatorShell>
    );
  }

  const tickets = data?.tickets ?? [];
  const totals = data?.totals;
  const lastScan = scanLog[0];
  const lastScanMinAgo = lastScan
    ? Math.max(0, Math.floor((Date.now() - new Date(lastScan.ran_at).getTime()) / 60000))
    : null;

  const tier1Rate =
    totals && totals.tier1_seen_today > 0
      ? Math.round((totals.tier1_auto_resolved_today / totals.tier1_seen_today) * 100)
      : 0;

  return (
    <OperatorShell breadcrumbs={[{ label: "Support Queue" }]}>

      {/* ── Page header ─────────────────────────────────────────────── */}
      <header
        className="bg-white border-b border-neutral-200 sticky top-[64px] z-40"
        style={{ boxShadow: "0 1px 0 rgba(15, 23, 42, 0.02)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div
                className="w-1 h-8 rounded-full"
                style={{ background: MI6_RED }}
              />
              <div>
                <div className="text-[9px] uppercase tracking-[0.22em] text-neutral-500 font-semibold">
                  Mi6 · Attilius · Admin
                </div>
                <h1 className="text-base font-bold text-neutral-900 leading-none mt-0.5">
                  Support Queue · Attilius
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchAll}
                className="text-[11px] px-3 py-1.5 rounded-full bg-white border border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:text-neutral-800 font-medium"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* ── Filter pills ───────────────────────────────────────── */}
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
              <span className="text-[9px] uppercase tracking-[0.22em] text-neutral-500 font-semibold mr-1 whitespace-nowrap hidden sm:block">
                Status
              </span>
              <FilterPill active={statusFilter === "all"} onClick={() => setStatusFilter("all")}>
                All
              </FilterPill>
              <FilterPill active={statusFilter === "open"} onClick={() => setStatusFilter("open")}>
                Open
              </FilterPill>
              <FilterPill
                active={statusFilter === "in_progress"}
                onClick={() => setStatusFilter("in_progress")}
              >
                In Progress
              </FilterPill>
              <FilterPill
                active={statusFilter === "resolved"}
                onClick={() => setStatusFilter("resolved")}
              >
                Resolved
              </FilterPill>
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
              <span className="text-[9px] uppercase tracking-[0.22em] text-neutral-500 font-semibold mr-1 whitespace-nowrap hidden sm:block">
                Tier
              </span>
              <FilterPill active={tierFilter === "all"} onClick={() => setTierFilter("all")}>
                All Tiers
              </FilterPill>
              <FilterPill active={tierFilter === 1} onClick={() => setTierFilter(1)}>
                Tier 1 (Computer)
              </FilterPill>
              <FilterPill active={tierFilter === 2} onClick={() => setTierFilter(2)}>
                Tier 2 (Yvonne)
              </FilterPill>
              <FilterPill active={tierFilter === 3} onClick={() => setTierFilter(3)}>
                Tier 3 (Bryan)
              </FilterPill>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {error && (
          <div className="mb-4 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* ── KPI strip ────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiTile
            label="Open Tickets"
            value={String(totals?.open ?? 0)}
            sub={`${totals?.in_progress ?? 0} in progress · ${totals?.resolved ?? 0} resolved`}
            accent="navy"
          />
          <KpiTile
            label="Tier 1 Auto-Resolved Today"
            value={String(totals?.tier1_auto_resolved_today ?? 0)}
            sub={`${tier1Rate}% auto-resolution rate`}
            accent="navy"
          />
          <KpiTile
            label="Tier 2 Awaiting Yvonne"
            value={String(totals?.tier2_open ?? 0)}
            sub={`Oldest: ${ageMinutesToText(totals?.tier2_oldest_age_min ?? null)}`}
            accent="slate"
          />
          <KpiTile
            label="Tier 3 With Bryan"
            value={String(totals?.tier3_open ?? 0)}
            sub={`Oldest: ${ageMinutesToText(totals?.tier3_oldest_age_min ?? null)}`}
            accent="red"
            valueColor={(totals?.tier3_open ?? 0) > 0 ? MI6_RED : undefined}
          />
        </div>

        {/* ── Main table / cards ───────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: MI6_SLATE }}>
              Tickets
            </div>
            <div className="text-[10px] text-neutral-500">
              {loading ? "Loading…" : `${tickets.length} shown`}
            </div>
          </div>

          {loading && tickets.length === 0 ? (
            <div className="px-5 py-10 text-center text-xs text-neutral-500">Loading queue…</div>
          ) : tickets.length === 0 ? (
            <EmptyState lastScanMinAgo={lastScanMinAgo} />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-semibold border-b border-neutral-200">
                      <th className="px-4 py-2.5 text-left">Tier</th>
                      <th className="px-4 py-2.5 text-left">Status</th>
                      <th className="px-4 py-2.5 text-left">Subject</th>
                      <th className="px-4 py-2.5 text-left">Customer</th>
                      <th className="px-4 py-2.5 text-left">Page</th>
                      <th className="px-4 py-2.5 text-left">Created</th>
                      <th className="px-4 py-2.5 text-left">Assigned</th>
                      <th className="px-4 py-2.5 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t) => (
                      <TicketRow
                        key={t.id}
                        ticket={t}
                        expanded={expandedId === t.id}
                        onToggle={() => setExpandedId(expandedId === t.id ? null : t.id)}
                        onReRun={() => reRunAutoHandler(t.id)}
                        onPatch={(p) => patchTicket(t.id, p)}
                        busy={busyId === t.id}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-neutral-200">
                {tickets.map((t) => (
                  <MobileTicketCard
                    key={t.id}
                    ticket={t}
                    expanded={expandedId === t.id}
                    onToggle={() => setExpandedId(expandedId === t.id ? null : t.id)}
                    onReRun={() => reRunAutoHandler(t.id)}
                    onPatch={(p) => patchTicket(t.id, p)}
                    busy={busyId === t.id}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── 15-min scan log strip ────────────────────────────────── */}
        <div className="mt-6 bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div
                className="text-[10px] uppercase tracking-[0.18em] font-bold"
                style={{ color: MI6_SLATE }}
              >
                15-min scan log
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">
                {lastScan
                  ? `Last scan: ${lastScanMinAgo}m ago · ${lastScan.tickets_scanned} tickets processed`
                  : "No scans yet."}
              </div>
            </div>
          </div>
          {scanLog.length === 0 ? (
            <div className="text-[11px] text-neutral-500 py-2">No scan history available.</div>
          ) : (
            <div className="flex items-end gap-1.5 overflow-x-auto pb-1">
              {scanLog
                .slice()
                .reverse()
                .map((s) => {
                  const hot = (s.escalations_fired ?? 0) > 0;
                  return (
                    <div
                      key={s.id}
                      className="flex-shrink-0 flex flex-col items-center gap-1"
                      title={`${new Date(s.ran_at).toLocaleString()} · ${s.tickets_scanned} scanned · ${s.escalations_fired} escalations`}
                    >
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ background: hot ? MI6_RED : "#C9C9CC" }}
                      />
                      <div
                        className="rounded-sm"
                        style={{
                          width: "10px",
                          height: `${Math.max(6, Math.min(40, (s.tickets_scanned ?? 0) * 4))}px`,
                          background: hot ? "#FCA5A555" : "#E5E5E7",
                          border: `1px solid ${hot ? MI6_RED : "#C9C9CC"}`,
                        }}
                      />
                      <div className="text-[9px] text-neutral-500 tabular-nums">
                        {new Date(s.ran_at).toLocaleTimeString("en-US", {
                          timeZone: "America/Detroit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </main>
    </OperatorShell>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────
function EmptyState({ lastScanMinAgo }: { lastScanMinAgo: number | null }) {
  return (
    <div className="px-6 py-12 text-center">
      <div className="text-sm font-semibold text-neutral-900 mb-1">No open tickets.</div>
      <div className="text-xs text-neutral-500">
        {lastScanMinAgo == null
          ? "The scan has not run yet."
          : `The 15-minute scan ran ${lastScanMinAgo}m ago.`}
      </div>
    </div>
  );
}

// ─── Ticket row (desktop) ────────────────────────────────────────────────────
function TicketRow({
  ticket,
  expanded,
  onToggle,
  onReRun,
  onPatch,
  busy,
}: {
  ticket: SupportTicket;
  expanded: boolean;
  onToggle: () => void;
  onReRun: () => void;
  onPatch: (p: Partial<Pick<SupportTicket, "status" | "tier" | "assigned_to">>) => void;
  busy: boolean;
}) {
  const tier = (ticket.tier ?? 1) as Tier;
  const tc = tierColors(tier);
  const sc = statusColors(ticket.status);

  return (
    <>
      <tr
        className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
            style={{ background: tc.bg, color: tc.fg, border: `1px solid ${tc.border}` }}
          >
            {tc.label}
          </span>
        </td>
        <td className="px-4 py-3">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
            style={{ background: sc.bg, color: sc.fg }}
          >
            {sc.label}
          </span>
        </td>
        <td className="px-4 py-3 max-w-[280px]">
          <div className="text-xs font-semibold text-neutral-900 truncate">{ticket.subject}</div>
        </td>
        <td className="px-4 py-3 text-xs text-neutral-600 max-w-[180px] truncate">
          {ticket.user_email ?? "—"}
        </td>
        <td className="px-4 py-3 text-xs text-neutral-500 max-w-[160px] truncate">
          {ticket.page ?? "—"}
        </td>
        <td className="px-4 py-3 text-xs text-neutral-500 whitespace-nowrap">
          {relTime(ticket.created_at)}
        </td>
        <td className="px-4 py-3 text-xs text-neutral-600 whitespace-nowrap">
          {assignedLabel(ticket.assigned_to ?? null)}
        </td>
        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          <ActionButton ticket={ticket} onReRun={onReRun} onPatch={onPatch} busy={busy} />
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-neutral-200 bg-neutral-50/60">
          <td colSpan={8} className="px-5 py-4">
            <ExpandedDetail ticket={ticket} onPatch={onPatch} busy={busy} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Mobile card row ─────────────────────────────────────────────────────────
function MobileTicketCard({
  ticket,
  expanded,
  onToggle,
  onReRun,
  onPatch,
  busy,
}: {
  ticket: SupportTicket;
  expanded: boolean;
  onToggle: () => void;
  onReRun: () => void;
  onPatch: (p: Partial<Pick<SupportTicket, "status" | "tier" | "assigned_to">>) => void;
  busy: boolean;
}) {
  const tier = (ticket.tier ?? 1) as Tier;
  const tc = tierColors(tier);
  const sc = statusColors(ticket.status);

  return (
    <div className="px-4 py-3 cursor-pointer" onClick={onToggle}>
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
          style={{ background: tc.bg, color: tc.fg, border: `1px solid ${tc.border}` }}
        >
          {tc.label}
        </span>
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ background: sc.bg, color: sc.fg }}
        >
          {sc.label}
        </span>
        <span className="ml-auto text-[10px] text-neutral-500">{relTime(ticket.created_at)}</span>
      </div>
      <div className="text-xs font-semibold text-neutral-900 mb-0.5">{ticket.subject}</div>
      <div className="text-[11px] text-neutral-500 truncate">{ticket.user_email ?? "—"}</div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[10px] text-neutral-500">{assignedLabel(ticket.assigned_to ?? null)}</span>
        <span onClick={(e) => e.stopPropagation()}>
          <ActionButton ticket={ticket} onReRun={onReRun} onPatch={onPatch} busy={busy} />
        </span>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-neutral-200">
          <ExpandedDetail ticket={ticket} onPatch={onPatch} busy={busy} />
        </div>
      )}
    </div>
  );
}

// ─── Action button (per tier) ───────────────────────────────────────────────
function ActionButton({
  ticket,
  onReRun,
  onPatch,
  busy,
}: {
  ticket: SupportTicket;
  onReRun: () => void;
  onPatch: (p: Partial<Pick<SupportTicket, "status" | "tier" | "assigned_to">>) => void;
  busy: boolean;
}) {
  const tier = (ticket.tier ?? 1) as Tier;
  const baseClass =
    "inline-flex items-center px-3 py-1.5 rounded-md text-[11px] font-semibold whitespace-nowrap transition-colors";

  if (ticket.status === "resolved") {
    return <span className="text-[10px] text-neutral-500 italic">Resolved</span>;
  }

  if (tier === 3) {
    return (
      <a
        href="https://slack.com/app_redirect?channel=U0AVAGZUPAN"
        target="_blank"
        rel="noopener noreferrer"
        className={`${baseClass} bg-[#DC2626] hover:bg-[#B91C1C] text-white`}
      >
        Open in Slack
      </a>
    );
  }

  if (tier === 2) {
    if (ticket.status === "in_progress") {
      return (
        <button
          disabled={busy}
          onClick={() => onPatch({ status: "resolved", resolved_at: new Date().toISOString() } as any)}
          className={`${baseClass} bg-[#0B0B0F] hover:bg-[#374151] text-white disabled:opacity-50`}
        >
          Mark Resolved
        </button>
      );
    }
    return (
      <button
        disabled={busy}
        onClick={() => onPatch({ status: "in_progress" })}
        className={`${baseClass} bg-[#0B0B0F] hover:bg-[#374151] text-white disabled:opacity-50`}
      >
        Mark in Progress
      </button>
    );
  }

  // Tier 1
  return (
    <button
      disabled={busy}
      onClick={onReRun}
      className={`${baseClass} bg-white border border-neutral-300 text-neutral-700 hover:border-neutral-400 disabled:opacity-50`}
    >
      {busy ? "Running…" : "Re-run Auto-Handler"}
    </button>
  );
}

// ─── Expanded detail panel ───────────────────────────────────────────────────
function ExpandedDetail({
  ticket,
  onPatch,
  busy,
}: {
  ticket: SupportTicket;
  onPatch: (p: Partial<Pick<SupportTicket, "status" | "tier" | "assigned_to">>) => void;
  busy: boolean;
}) {
  const audit = Array.isArray(ticket.audit_log) ? ticket.audit_log : [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Description + screenshot */}
      <div className="md:col-span-2 space-y-3">
        <div>
          <div
            className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1"
            style={{ color: MI6_SLATE }}
          >
            Description
          </div>
          <div className="text-xs text-neutral-700 whitespace-pre-wrap leading-relaxed">
            {ticket.description}
          </div>
        </div>

        {ticket.screenshot_url && (
          <div>
            <div
              className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1"
              style={{ color: MI6_SLATE }}
            >
              Screenshot
            </div>
            <a href={ticket.screenshot_url} target="_blank" rel="noopener noreferrer">
              <img
                src={ticket.screenshot_url}
                alt="Screenshot"
                className="max-h-64 rounded-lg border border-neutral-200"
              />
            </a>
          </div>
        )}

        <div>
          <div
            className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1"
            style={{ color: MI6_SLATE }}
          >
            Audit log
          </div>
          {audit.length === 0 ? (
            <div className="text-[11px] text-neutral-500">
              {ticket.auto_response_sent
                ? "Auto-response sent."
                : "No automated actions recorded yet."}
            </div>
          ) : (
            <ul className="space-y-1">
              {audit.map((a, i) => (
                <li key={i} className="text-[11px] text-neutral-600">
                  <span className="text-neutral-500 mr-2 tabular-nums">{relTime(a.at)}</span>
                  <span className="font-semibold text-neutral-700">{a.actor}</span>
                  <span className="text-neutral-500"> · {a.action}</span>
                  {a.detail ? <span className="text-neutral-500"> — {a.detail}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-3">
        <div>
          <div
            className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1"
            style={{ color: MI6_SLATE }}
          >
            Reassign tier
          </div>
          <select
            value={ticket.tier ?? 1}
            disabled={busy}
            onChange={(e) =>
              onPatch({
                tier: Number(e.target.value) as Tier,
                assigned_to:
                  Number(e.target.value) === 1
                    ? "computer"
                    : Number(e.target.value) === 2
                    ? "yvonne"
                    : "bryan",
              })
            }
            className="w-full text-xs px-2 py-1.5 rounded-md border border-neutral-300 bg-white text-neutral-700 focus:border-neutral-500 outline-none"
          >
            <option value={1}>Tier 1 (Computer)</option>
            <option value={2}>Tier 2 (Yvonne)</option>
            <option value={3}>Tier 3 (Bryan)</option>
          </select>
        </div>

        <div>
          <div
            className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1"
            style={{ color: MI6_SLATE }}
          >
            Status
          </div>
          <select
            value={ticket.status}
            disabled={busy}
            onChange={(e) =>
              onPatch({
                status: e.target.value as Status,
                ...(e.target.value === "resolved"
                  ? ({ resolved_at: new Date().toISOString() } as any)
                  : {}),
              })
            }
            className="w-full text-xs px-2 py-1.5 rounded-md border border-neutral-300 bg-white text-neutral-700 focus:border-neutral-500 outline-none"
          >
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>

        {ticket.status !== "resolved" && (
          <button
            disabled={busy}
            onClick={() =>
              onPatch({
                status: "resolved",
                ...(({ resolved_at: new Date().toISOString() } as any) as object),
              })
            }
            className="w-full inline-flex items-center justify-center px-3 py-2 rounded-md text-[11px] font-semibold bg-[#0B0B0F] hover:bg-[#374151] text-white disabled:opacity-50"
          >
            Mark Resolved
          </button>
        )}
      </div>
    </div>
  );
}
